import { Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { DatePipe } from '@angular/common';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import { LoaderService, MultilingualService } from '@core/services';
import { AppCenterAnalytics, AppInsightsService, CmsService, DeleteMemberService } from '@shared/services';
import { emailPattern } from '@shared/utils';
import { BrandConfig, DeleteMemberRequest } from '@models/interfaces';
import { DeleteAccountDialogComponent } from '../delete-account-dialog/delete-account-dialog.component';
import { BRAND_CONFIG } from '@core/injection-tokens';

@Component({
    selector: 'app-delete-member-account',
    templateUrl: './delete-member-account.component.html',
    styleUrls: ['./delete-member-account.component.scss'],
    providers: [DatePipe],
    standalone: false
})
export class DeleteMemberAccountComponent implements OnInit, OnDestroy {
  @ViewChild('success', { read: TemplateRef }) successTemplate!: TemplateRef<any>;
  @ViewChild('errors', { read: TemplateRef }) errorTemplate!: TemplateRef<any>;
  emailUpdate = false;
  successMsg!: string;
  errorMsg!: string;
  subscription: Subscription = new Subscription();
  basicInfo = true;
  basicInfoWithoutVerification = true;
  isVerificationCode  = false;
  reasonForLeaving = false;
  deleteAccountConfirm = false;
  deleteAccountForm!: FormGroup;
  isDeleteFlow = true;
  maxDate = new Date();

  get appName(): string {
    return this.brandConfig.displayName;
  }

  constructor(public readonly dialog: MatDialog,
    public readonly multilingualService: MultilingualService,
    public readonly appInsightsService: AppInsightsService,
    private readonly deleteMemberService: DeleteMemberService,
    private readonly snackBar: MatSnackBar,
    private readonly loader: LoaderService,
    private readonly datePipe:DatePipe,
    @Inject(BRAND_CONFIG) private readonly brandConfig: BrandConfig,
    public cmsService: CmsService
  ) { }

  ngOnInit(): void {
    this.appInsightsService.trackPageViewWithoutClientId(AppCenterAnalytics.DELETE_MEMBER_ACCOUNT_PAGE_VIEWED);
    this.deleteAccountForm = new FormGroup({
      userName: new FormControl('', [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(250),
      ]),
      email: new FormControl('', [
        Validators.required, Validators.pattern(emailPattern)
      ]),
      verificationCode: new FormControl('', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
      ]),
      leavingReason: new FormControl('', [
        Validators.required,
      ]),
      additionComment: new FormControl('', [
        Validators.minLength(2),
        Validators.maxLength(50)
      ]),
      DOB: new FormControl('', [
        Validators.required,
      ])
    });
  }

  public selectReason(value:string): void {
    if(value == 'other'){
      this.deleteAccountForm.controls.additionComment.setValidators([Validators.required,Validators.minLength(2),
        Validators.maxLength(50)]);
      this.deleteAccountForm.controls.additionComment.updateValueAndValidity();
      this.deleteAccountForm.updateValueAndValidity();
    } else {
      this.deleteAccountForm.controls.additionComment.setValidators([]);
      this.deleteAccountForm.controls.additionComment.updateValueAndValidity();
      this.deleteAccountForm.updateValueAndValidity();
    }
  }
  public goBack(): void {
    if(this.reasonForLeaving) {
      this.basicInfo = true;
      this.basicInfoWithoutVerification = true
      this.isVerificationCode = false;
      this.reasonForLeaving = false;
      this.deleteAccountConfirm = false;
    } else {
      this.reasonForLeaving = true;
      this.basicInfo = false;
      this.basicInfoWithoutVerification = false;
      this.isVerificationCode = false;
      this.deleteAccountConfirm = false;
    }
  }
  public checkIfSpace(number: any): boolean {
    const onlySpace = number.which ? number.which : number.keyCode;
    return (onlySpace < 48 || onlySpace > 57) ? false : true;
  }
  public checkEmailUpdateEvent(event: any): void {
    this.basicInfo = true;
    this.basicInfoWithoutVerification = true;
      if (this.deleteAccountForm.controls['email'].value !== 'test@gmail.com') {
        this.deleteAccountForm.controls['verificationCode'].patchValue('');
        this.isVerificationCode = false;
    }
  }
  public sendVerificationCode(): void {
    this.deleteAccountForm.controls['verificationCode'].patchValue('');
    this.appInsightsService.trackPageViewWithoutClientId(AppCenterAnalytics.DELETE_MEMBER_ACCOUNT_SEND_OTP_BUTTON_CLICKED);
    this.loader.show();
    this.subscription.add(this.deleteMemberService.sendOTP(this.deleteAccountForm.controls.email.value, this.deleteAccountForm.controls.userName.value)
    .subscribe((res)=>{
    if(res) {
      this.appInsightsService.trackPageViewWithoutClientId(AppCenterAnalytics.DELETE_MEMBER_ACCOUNT_SEND_OTP_SUCCESS, {Response: res });
      this.loader.hide();
      this.successMsg = res;
      this.snackBar.openFromTemplate(this.successTemplate, {
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
      this.basicInfo = true;
      this.basicInfoWithoutVerification = false;
      this.isVerificationCode = true;
      this.reasonForLeaving = false;
      this.deleteAccountConfirm = false;
    }
    },(error)=> {
      this.loader.hide();
      this.appInsightsService.trackPageViewWithoutClientId(AppCenterAnalytics.DELETE_MEMBER_ACCOUNT_SEND_OTP_ERROR, {Response: error });

      this.errorMsg = error;
      this.snackBar.openFromTemplate(this.errorTemplate, {
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
    }))
      
  }

  public verify() : void {
    this.appInsightsService.trackPageViewWithoutClientId(AppCenterAnalytics.DELETE_MEMBER_ACCOUNT_VERIFY_OTP_BUTTON_CLICKED);
    this.loader.show();
    this.subscription.add(this.deleteMemberService.verifyOTP(this.deleteAccountForm.controls.verificationCode.value, this.deleteAccountForm.controls.userName.value)
    .subscribe((data)=>{
    if(data) {
      this.appInsightsService.trackPageViewWithoutClientId(AppCenterAnalytics.DELETE_MEMBER_ACCOUNT_VERIFY_OTP_SUCCESS, {Response: data });
      this.successMsg = data;
      this.loader.hide();
      this.snackBar.openFromTemplate(this.successTemplate, {
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
      this.basicInfo = false;
      this.isVerificationCode = false;
      this.reasonForLeaving = true;
      this.deleteAccountConfirm = false;
    }
    },(error)=> {
      this.loader.hide();
      this.appInsightsService.trackPageViewWithoutClientId(AppCenterAnalytics.DELETE_MEMBER_ACCOUNT_VERIFY_OTP_ERROR, {Response: error });
      this.errorMsg = error;
      this.snackBar.openFromTemplate(this.errorTemplate, {
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
    }))
    
  }

  public next(): void {
    this.appInsightsService.trackPageViewWithoutClientId(AppCenterAnalytics.DELETE_MEMBER_ACCOUNT_NEXT_BUTTON_CLICKED, this.getProperties());
    this.basicInfo = false;
    this.isVerificationCode = false;
    this.reasonForLeaving = false;
    this.deleteAccountConfirm = true;
  
  }

  public resend(): void {
    this.deleteAccountForm.controls['verificationCode'].patchValue('');
    this.loader.show();
    this.appInsightsService.trackPageViewWithoutClientId(AppCenterAnalytics.DELETE_MEMBER_ACCOUNT_RE_SEND_OTP_BUTTON_CLICKED);
    this.subscription.add(this.deleteMemberService.sendOTP(this.deleteAccountForm.controls.email.value, this.deleteAccountForm.controls.userName.value)
    .subscribe((res)=>{
    if(res) {
      this.appInsightsService.trackPageViewWithoutClientId(AppCenterAnalytics.DELETE_MEMBER_ACCOUNT_RESEND_OTP_SUCCESS, {Response: res });
      this.loader.hide();
      this.successMsg = res;
      this.snackBar.openFromTemplate(this.successTemplate, {
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
      this.basicInfo = true;
      this.basicInfoWithoutVerification = false;
      this.isVerificationCode = true;
      this.reasonForLeaving = false;
      this.deleteAccountConfirm = false;
    }
    },(error)=> {
      this.loader.hide();
      this.appInsightsService.trackPageViewWithoutClientId(AppCenterAnalytics.DELETE_MEMBER_ACCOUNT_RESEND_OTP_ERROR, {Response: error });

      this.errorMsg = error;
      this.snackBar.openFromTemplate(this.errorTemplate, {
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'center'
      });
    }))
  } 
  public deleteAccount(): void {
    this.appInsightsService.trackPageViewWithoutClientId(AppCenterAnalytics.DELETE_MEMBER_ACCOUNT_SUBMIT_DELETE_BUTTON_CLICKED, this.getProperties()); 
    const dialogRef = this.dialog.open(DeleteAccountDialogComponent, { disableClose: true, data: {title:'confirmation'} });
    dialogRef.afterClosed().subscribe((result) => {
      if(result) {
        this.loader.show();
        const deleteReq: DeleteMemberRequest = {
          userName:this.deleteAccountForm.value.userName,
          email: this.deleteAccountForm.value.email,
          leavingReason: this.deleteAccountForm.value.leavingReason,
          additionComment: this.deleteAccountForm.value.additionComment,
          DOB:this.datePipe.transform(this.deleteAccountForm.value.DOB,'MM-dd-yyyy')
        }
        this.subscription.add(this.deleteMemberService.deleteMember(deleteReq)
        .subscribe((res)=>{
          this.appInsightsService.trackPageViewWithoutClientId(AppCenterAnalytics.DELETE_MEMBER_ACCOUNT_SUBMIT_DELETE_MEMBER_SUCCESS,{Response: res});
          this.loader.hide();
          this.successMsg = res;
          this.reasonForLeaving = false;
          this.deleteAccountConfirm = false
          this.isDeleteFlow = false;
        },(error)=>{
          this.appInsightsService.trackPageViewWithoutClientId(AppCenterAnalytics.DELETE_MEMBER_ACCOUNT_SUBMIT_DELETE_MEMBER_ERROR,{Response: error});
          this.loader.hide();
          this.errorMsg = error;
          this.snackBar.openFromTemplate(this.errorTemplate, {
            duration: 4000,
            verticalPosition: 'top',
            horizontalPosition: 'center'
          });
        }))
      }
    });
  }

  private getProperties(): Record<string, any> {
    const properties: Record<string, any> = {
      userID: this.deleteAccountForm.value.userName,
      Email: this.deleteAccountForm.value.email,
      data: {
        message: this.deleteAccountForm.value.leavingReason,
        birthdate: this.datePipe.transform(this.deleteAccountForm.value.DOB,'MM-dd-yyyy')
      }
    }
    return properties;
  }

  ngOnDestroy(): void {
    if(this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
